/**
 * Formats an ISO 8601/RFC 3339 UTC time string into a human-readable local time string.
 *
 * @param {string} timeString - The input time string (e.g., "2025-06-23T11:30:00Z").
 * @returns {string} The formatted time string (e.g., "June 23, 2025 at 11:30 AM") or "Invalid Date" if parsing fails.
 */
export const formatUTCToLocalDisplayTime = (timeString: string) => {
  const date = new Date(timeString);

  // Check if the date object is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  // Options for formatting the date part
  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long', // e.g., "June"
    day: 'numeric', // e.g., "23"
  };

  // Options for formatting the time part
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric', // e.g., "11"
    minute: 'numeric', // e.g., "30"
    hour12: true, // Use 12-hour format (AM/PM)
  };

  // Create formatters for each part
  const dateFormatter = new Intl.DateTimeFormat(undefined, dateOptions); // `undefined` uses default locale
  const timeFormatter = new Intl.DateTimeFormat(undefined, timeOptions);

  // Format the parts and combine them
  const formattedDate = dateFormatter.format(date);
  const formattedTime = timeFormatter.format(date);

  return `${formattedDate} at ${formattedTime}`;
};

// --- Example Usage in a React Component ---
/*
import React from 'react';
import { formatUTCToLocalDisplayTime } from './your-time-utility-file'; // Adjust path as needed

const MyComponent = () => {
  const utcTime = "2025-06-23T11:30:00Z";
  const localDisplayTime = formatUTCToLocalDisplayTime(utcTime);

  const anotherUtcTime = "2024-01-01T00:00:00Z";
  const anotherLocalDisplayTime = formatUTCToLocalDisplayTime(anotherUtcTime);

  const invalidTime = "not-a-date";
  const invalidDisplayTime = formatUTCToLocalDisplayTime(invalidTime);

  return (
    <div>
      <p>UTC Time: {utcTime}</p>
      <p>Formatted Local Time: {localDisplayTime}</p>
      <br/>
      <p>Another UTC Time: {anotherUtcTime}</p>
      <p>Formatted Another Local Time: {anotherLocalDisplayTime}</p>
      <br/>
      <p>Invalid Time: {invalidTime}</p>
      <p>Formatted Invalid Time: {invalidDisplayTime}</p>
    </div>
  );
};

export default MyComponent;
*/